<template>
  <section class="personal-center-panel">
    <h2>账户安全</h2>
    <div v-if="front.needsForcePasswordChange" class="service-notice warning">
      <strong>请先修改密码</strong>
      <span>{{ passwordHint }}。当前口令已过期、不合规或为初始密码，完成修改前暂不可使用其他功能。</span>
    </div>
    <p v-else-if="front.pwdWarn" class="service-notice">口令将于 {{ front.pwdDaysLeft }} 天后过期，请尽快修改。</p>
    <label class="profile-field">原密码<input v-model="front.passwordForm.oldPassword" type="password" autocomplete="current-password" /></label>
    <label class="profile-field">
      新密码
      <input
        v-model="front.passwordForm.newPassword"
        type="password"
        autocomplete="new-password"
        maxlength="32"
        @input="onNewPasswordInput"
      />
      <small class="auth-label-note">{{ passwordHint }}</small>
      <small v-if="policyError" class="auth-feedback warning" role="alert">{{ policyError }}</small>
    </label>
    <label class="profile-field">确认新密码<input v-model="front.passwordForm.confirmPassword" type="password" autocomplete="new-password" /></label>
    <div class="security-actions">
      <button type="button" class="primary-action compact" @click="front.submitChangePassword">保存新密码</button>
      <button type="button" class="ghost-action compact" @click="front.logout">退出登录</button>
    </div>
    <p
      v-if="passwordStatus"
      ref="passwordStatus"
      class="service-notice security-feedback"
      :class="statusNoticeClass(passwordStatus)"
      role="alert"
    >
      {{ passwordStatus }}
    </p>
    <div v-if="!front.needsForcePasswordChange" class="external-account-block">
      <div class="external-account-head">
        <div>
          <h3>中国科技云账号</h3>
          <p>用于安全登录本站，不会将科技云令牌保存到浏览器。</p>
        </div>
        <span class="binding-state" :class="{ active: bindingActive }">{{ bindingActive ? '已绑定' : '未绑定' }}</span>
      </div>
      <p v-if="bindingLoading" class="service-notice">正在读取绑定状态...</p>
      <template v-else-if="bindingActive">
        <dl class="binding-summary">
          <div><dt>身份</dt><dd>{{ binding.externalNameMasked || binding.externalName || '已认证科技云用户' }}</dd></div>
          <div><dt>绑定时间</dt><dd>{{ binding.boundTime || '—' }}</dd></div>
          <div><dt>最近登录</dt><dd>{{ binding.lastLoginTime || '暂无记录' }}</dd></div>
        </dl>
        <div class="security-actions">
          <button type="button" class="ghost-action compact" :disabled="providerBusy" @click="startProviderAction(true)">重新授权</button>
          <button type="button" class="ghost-action compact danger-action" :disabled="providerBusy" @click="unbindOpen = !unbindOpen">解除绑定</button>
        </div>
        <div v-if="unbindOpen" class="unbind-box">
          <p class="service-notice warning">解除绑定后当前会话可能退出。若科技云是唯一登录方式，请先设置本站登录密码。</p>
          <div class="unbind-methods">
            <button type="button" :class="{ selected: unbindMethod === 'PASSWORD' }" @click="unbindMethod = 'PASSWORD'">密码验证</button>
            <button type="button" :class="{ selected: unbindMethod === 'EMAIL_CODE' }" @click="unbindMethod = 'EMAIL_CODE'">邮箱验证码</button>
          </div>
          <label v-if="unbindMethod === 'PASSWORD'" class="profile-field">当前密码<input v-model="unbindPassword" type="password" autocomplete="current-password" /></label>
          <div v-else class="unbind-code-row">
            <label class="profile-field">邮箱验证码<input v-model.trim="unbindCode" inputmode="numeric" maxlength="6" /></label>
            <button type="button" class="ghost-action compact" :disabled="codeSending || codeSeconds > 0" @click="sendUnbindCode">{{ codeButtonText }}</button>
          </div>
          <button type="button" class="ghost-action compact danger-action" :disabled="providerBusy" @click="confirmUnbind">确认解除绑定</button>
        </div>
      </template>
      <button v-else type="button" class="primary-action compact" :disabled="providerBusy" @click="startProviderAction(false)">绑定中国科技云</button>
      <p v-if="providerFeedback" class="service-notice security-feedback" :class="statusNoticeClass(providerFeedback)" role="alert">{{ providerFeedback }}</p>
    </div>
    <div v-if="!front.needsForcePasswordChange" class="account-cancel-block">
      <h3 class="account-cancel-title">注销账号</h3>
      <p class="service-notice warning account-cancel-notice">
        注销后您将无法再使用本账号登录。会费与会议相关记录将依法保留并匿名化处理。若存在进行中的入会/退会申请、会费缴纳或会议报名，须先处理完成或联系秘书处后再注销。
      </p>
      <label class="account-cancel-check">
        <input v-model="front.cancelAccountForm.acknowledged" type="checkbox" />
        <span>我已知晓上述后果</span>
      </label>
      <div class="account-cancel-form">
        <label class="profile-field" for="cancel-account-password">
          <span>当前密码</span>
          <input
            id="cancel-account-password"
            v-model="front.cancelAccountForm.password"
            type="password"
            autocomplete="current-password"
          />
        </label>
        <label class="profile-field account-cancel-confirm" for="cancel-account-confirm-text">
          <span>请输入“确认注销”</span>
          <input
            id="cancel-account-confirm-text"
            ref="cancelConfirmInput"
            v-model="front.cancelAccountForm.confirmText"
            type="text"
            autocomplete="off"
            maxlength="4"
            placeholder="请输入“确认注销”"
          />
          <small>必须完整输入“确认注销”后才能提交。</small>
        </label>
      </div>
      <button
        type="button"
        class="ghost-action compact danger-action"
        :disabled="!front.cancelAccountForm.acknowledged"
        @click="submitCancelAccount"
      >
        注销账号
      </button>
      <p
        v-if="cancelStatus"
        class="service-notice security-feedback"
        :class="statusNoticeClass(cancelStatus)"
        role="alert"
      >
        {{ cancelStatus }}
      </p>
    </div>
  </section>
</template>

<script>
import { passwordPolicyHint, validatePasswordPolicy } from '../../../utils/passwordPolicy'
import {
  getCstCloudBinding,
  reauthorizeCstCloudBinding,
  sendEmailCode,
  startCstCloudBinding,
  unbindCstCloudBinding
} from '../../../api/paleo'
import { getAuthorizationUrl, isSafeAuthorizationUrl, unwrapApiData } from '../../../utils/cstCloudAuth'

export default {
  name: 'PersonalSecurityPanel',
  inject: ['front'],
  data() {
    return {
      policyError: '',
      bindingLoading: true,
      binding: {},
      providerBusy: false,
      providerFeedback: '',
      unbindOpen: false,
      unbindMethod: 'PASSWORD',
      unbindPassword: '',
      unbindCode: '',
      codeSending: false,
      codeSeconds: 0,
      codeTimer: null
    }
  },
  created() {
    if (!this.front.needsForcePasswordChange) {
      this.front.selfServiceStatus = ''
      this.loadBinding()
    }
  },
  beforeDestroy() {
    if (this.codeTimer) clearInterval(this.codeTimer)
  },
  watch: {
    passwordStatus(value) {
      if (!value) return
      this.$nextTick(() => {
        const el = this.$refs.passwordStatus
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      })
    }
  },
  computed: {
    passwordHint() {
      return passwordPolicyHint()
    },
    passwordStatus() {
      const text = this.front.selfServiceStatus || ''
      if (!text || this.isCancelStatus(text)) return ''
      return text
    },
    cancelStatus() {
      const text = this.front.selfServiceStatus || ''
      if (!text || !this.isCancelStatus(text)) return ''
      return text
    },
    bindingActive() {
      const status = String(this.binding.bindStatus || this.binding.status || '').toUpperCase()
      return this.binding.bound === true || this.binding.active === true || ['ACTIVE', 'BOUND', 'BOUND_ACTIVE'].includes(status)
    },
    codeButtonText() {
      if (this.codeSending) return '发送中...'
      return this.codeSeconds > 0 ? `${this.codeSeconds}s` : '获取验证码'
    }
  },
  methods: {
    async loadBinding() {
      this.bindingLoading = true
      try {
        this.binding = unwrapApiData(await getCstCloudBinding()) || {}
      } catch (error) {
        this.providerFeedback = error.message || '绑定状态读取失败'
      } finally {
        this.bindingLoading = false
      }
    },
    async startProviderAction(reauthorize) {
      if (this.providerBusy) return
      this.providerBusy = true
      this.providerFeedback = ''
      try {
        const result = reauthorize
          ? await reauthorizeCstCloudBinding('/personal-center?section=security')
          : await startCstCloudBinding('/personal-center?section=security')
        const url = getAuthorizationUrl(result)
        if (!isSafeAuthorizationUrl(url)) throw new Error('科技云授权地址无效')
        window.location.assign(url)
      } catch (error) {
        this.providerFeedback = error.message || '暂时无法连接中国科技云'
        this.providerBusy = false
      }
    },
    async sendUnbindCode() {
      const email = this.front.userInfo && this.front.userInfo.email
      if (!email) { this.providerFeedback = '当前账号缺少可验证邮箱，请先完善资料'; return }
      this.codeSending = true
      try {
        await sendEmailCode(email, 'CSTCLOUD_UNBIND')
        this.providerFeedback = '验证码已发送，请查收邮箱'
        this.codeSeconds = 60
        this.codeTimer = setInterval(() => { this.codeSeconds -= 1; if (this.codeSeconds <= 0) { clearInterval(this.codeTimer); this.codeTimer = null } }, 1000)
      } catch (error) {
        this.providerFeedback = error.message || '验证码发送失败'
      } finally {
        this.codeSending = false
      }
    },
    async confirmUnbind() {
      if (this.unbindMethod === 'PASSWORD' && !this.unbindPassword) { this.providerFeedback = '请输入当前密码'; return }
      if (this.unbindMethod === 'EMAIL_CODE' && !/^\d{6}$/.test(this.unbindCode)) { this.providerFeedback = '请输入 6 位邮箱验证码'; return }
      this.providerBusy = true
      try {
        await unbindCstCloudBinding({ verificationType: this.unbindMethod, password: this.unbindMethod === 'PASSWORD' ? this.unbindPassword : undefined, verificationCode: this.unbindMethod === 'EMAIL_CODE' ? this.unbindCode : undefined })
        this.providerFeedback = '科技云账号已解除绑定，请重新登录'
        this.unbindOpen = false
        await this.front.logout()
      } catch (error) {
        this.providerFeedback = error.message || '解除绑定失败'
      } finally {
        this.providerBusy = false
      }
    },
    onNewPasswordInput() {
      const value = this.front.passwordForm.newPassword || ''
      if (!value) {
        this.policyError = ''
        return
      }
      this.policyError = validatePasswordPolicy(value, this.front.userInfo && this.front.userInfo.email) || ''
    },
    async submitCancelAccount() {
      await this.front.submitCancelAccount()
      if (this.front.selfServiceStatus === '请输入：确认注销') {
        this.$nextTick(() => {
          const input = this.$refs.cancelConfirmInput
          if (input && typeof input.focus === 'function') input.focus()
        })
      }
    },
    isCancelStatus(text) {
      return /已注销|注销失败|勾选|确认注销|后再注销|请输入当前密码/.test(text)
    },
    statusNoticeClass(text) {
      if (/失败|请先|请填写|不能|不可|不一致|至少|过期|异常|错误|不正确|无权|保障|请输入|勾选/.test(text || '')) {
        return 'warning'
      }
      if (/已修改|成功|已重置|已保存|已注销/.test(text || '')) {
        return 'success'
      }
      return ''
    }
  }
}
</script>

<style scoped>
.security-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.security-feedback {
  margin-top: 12px;
  width: 100%;
  max-width: none;
  font-weight: 600;
}

.security-feedback.warning {
  border-color: #f0a8a0;
  background: #fff1f0;
  color: #b42318;
}

.security-feedback.success {
  border-color: #b7dcc5;
  background: #f0fbf4;
  color: #1d6b3a;
}

.account-cancel-block {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.external-account-block { margin-top: 1.5rem; padding-top: 1.25rem; border-top: 1px solid rgba(0, 0, 0, 0.08); }
.external-account-head { display: flex; justify-content: space-between; gap: 1rem; align-items: flex-start; }
.external-account-head h3 { margin: 0 0 .35rem; }
.external-account-head p { margin: 0 0 1rem; color: #64748b; }
.binding-state { flex: none; padding: .25rem .65rem; border-radius: 999px; color: #64748b; background: #eef2f6; }
.binding-state.active { color: #17643a; background: #e9f8ef; }
.binding-summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .75rem; margin: .5rem 0 1rem; }
.binding-summary div { padding: .75rem; border: 1px solid #e2e8f0; border-radius: 8px; }
.binding-summary dt { color: #64748b; font-size: .8rem; }
.binding-summary dd { margin: .3rem 0 0; }
.unbind-box { margin-top: 1rem; padding: 1rem; border: 1px solid #f0c7c2; border-radius: 8px; background: #fffafa; }
.unbind-methods { display: flex; gap: .5rem; margin: .75rem 0; }
.unbind-methods button { padding: .45rem .75rem; border: 1px solid #cbd5e1; border-radius: 6px; background: #fff; }
.unbind-methods button.selected { color: #0d5c91; border-color: #0d5c91; }
.unbind-code-row { display: flex; gap: .75rem; align-items: flex-end; }
@media (max-width: 700px) { .binding-summary { grid-template-columns: 1fr; } .unbind-code-row { display: block; } }

.account-cancel-title {
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  color: #b42318;
}

.account-cancel-notice {
  margin-bottom: 0.75rem;
}

.account-cancel-check {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
}

.account-cancel-check input {
  margin-top: 0.2rem;
}

.account-cancel-form {
  max-width: 460px;
  margin: 0.9rem 0 0.25rem;
  padding: 1rem;
  border: 1px solid #f0c7c2;
  border-radius: 8px;
  background: #fffafa;
}

.account-cancel-form .profile-field {
  max-width: none;
}

.account-cancel-confirm input {
  border-color: #d98f87;
  background: #fff;
}

.account-cancel-confirm input:focus {
  border-color: #b42318;
  outline: 3px solid rgba(180, 35, 24, 0.12);
}

.account-cancel-confirm small {
  color: #8a3a32;
  font-size: 12px;
  font-weight: 500;
}

.danger-action {
  color: #b42318;
  border-color: rgba(180, 35, 24, 0.35);
}

.danger-action:hover:not(:disabled) {
  color: #912018;
  border-color: rgba(180, 35, 24, 0.55);
  background: rgba(180, 35, 24, 0.06);
}

.danger-action:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
